import { useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SGTooltip } from 'components/core';
import { SymbolGroup } from 'types/canvas';
import CanvasSymSelector from 'components/canvas/components/CanvasSymSelector';
import { useCanvasGroupCtx } from './CanvasGroupContext';
import { EditGroupDialog } from './EditGroupDialog';
import {
  useGroupSymbolOptions,
  useGroupSymbolSet,
} from './useGroupSymbolOptions';
import { CanvasGroupWatchlistButton } from './CanvasGroupWatchlistButton';
import { GroupColorButton } from './GroupColorButton';
import { InlineEditableName } from './InlineEditableName';
import { GroupEditSheet } from './GroupEditSheet';

/**
 * Toolbar groups control: a Workspaces-style dropdown that purely SELECTS a
 * group, and an inline editor to its right for the targeted group — color,
 * name, symbol, watchlist, delete. On mobile the editor collapses into an
 * "Edit group" button that opens a full-screen sheet.
 */
export const CanvasGroupsBar = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const {
    groups,
    activeGroupId,
    setActiveGroupId,
    getGroupSym,
    broadcastSymToGroup,
    createGroup,
    renameGroup,
    recolorGroup,
    deleteGroup,
    pickDefaultColor,
  } = useCanvasGroupCtx();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SymbolGroup | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SymbolGroup | null>(null);
  const menuOpen = anchorEl != null;
  const closeMenu = () => setAnchorEl(null);

  const activeGroup = useMemo(
    () => groups.find((g) => g.id === activeGroupId) ?? null,
    [groups, activeGroupId],
  );
  // When nothing is explicitly pinned, target the first group so the editor
  // is always live.
  const effectiveGroup = activeGroup ?? groups[0] ?? null;
  const effectiveGroupId = effectiveGroup?.id ?? null;

  // Allowed symbols are scoped to what the targeted group's members support.
  const symOptions = useGroupSymbolOptions(effectiveGroup);
  const validSymbolSet = useGroupSymbolSet(effectiveGroup);

  const segmentDivider = (
    <Box
      sx={{
        alignSelf: 'stretch',
        width: '1px',
        backgroundColor: alpha(theme.palette.primary.main, 0.18),
      }}
    />
  );

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<WorkspacesIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
        endIcon={
          <ArrowDropDownIcon
            sx={{
              transition: 'transform 120ms',
              transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        aria-label="Symbol groups"
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: { xs: 12, md: 13 },
          letterSpacing: 0.2,
          minHeight: 44,
          px: { xs: 3, md: 6 },
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          },
          '& .label-text': { display: { xs: 'none', sm: 'inline' } },
          '& .MuiButton-startIcon': { mr: { xs: 0, sm: 3 } },
        }}
      >
        <Box component="span" className="label-text">
          Group Selection
        </Box>
      </Button>

      {/* Mobile: collapse the editor into a single "Edit group" button. */}
      {effectiveGroup != null && isNarrow && (
        <>
          {segmentDivider}
          <Button
            onClick={() => setSheetOpen(true)}
            startIcon={
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: effectiveGroup.color,
                }}
              />
            }
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              minHeight: 44,
              px: 2,
              color: theme.palette.text.primary,
            }}
          >
            Edit group
          </Button>
        </>
      )}

      {/* Desktop: inline editor for the targeted group. */}
      {effectiveGroup != null && !isNarrow && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              px: 3,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <GroupColorButton
                color={effectiveGroup.color}
                onChange={(color) => {
                  void recolorGroup(effectiveGroup.id, color);
                }}
              />
              <InlineEditableName
                value={effectiveGroup.name}
                onCommit={(name) => {
                  void renameGroup(effectiveGroup.id, name);
                }}
              />
            </Box>
            <CanvasSymSelector
              sym={getGroupSym(effectiveGroup.id) ?? ''}
              options={symOptions}
              onSymChange={(sym) => broadcastSymToGroup(effectiveGroup.id, sym)}
              textFieldStyleOverrides={{ width: 150 }}
            />
            <CanvasGroupWatchlistButton
              validSymbols={validSymbolSet}
              onPick={(sym) => broadcastSymToGroup(effectiveGroup.id, sym)}
            />
          </Box>
        </>
      )}

      {/* Group selector dropdown. */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
              maxWidth: 320,
              mt: 0.5,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              p: 0.5,
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                py: 0.875,
                px: 1.25,
                fontSize: 13,
              },
            },
          },
        }}
      >
        <Box sx={{ pt: 0.5, pb: 1, pr: 1.5, pl: 6 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: theme.palette.text.secondary,
              lineHeight: 1.2,
            }}
          >
            Symbol groups
          </Typography>
        </Box>

        {groups.length === 0 && (
          <Typography
            sx={{
              px: 1.5,
              py: 1,
              fontSize: 12.5,
              color: theme.palette.text.secondary,
            }}
          >
            No groups yet.
          </Typography>
        )}

        {groups.map((group) => {
          const isActive = group.id === effectiveGroupId;
          const memberCount = group.componentIds.length;
          return (
            <MenuItem
              key={group.id}
              selected={isActive}
              onClick={() => {
                setActiveGroupId(group.id);
                closeMenu();
              }}
              sx={{
                '& .group-row-actions': {
                  opacity: { xs: 1, md: 0 },
                  transition: 'opacity 120ms',
                },
                '&:hover .group-row-actions': { opacity: 1 },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 26 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: group.color,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={group.name}
                secondary={
                  memberCount === 0
                    ? 'No components'
                    : `${memberCount} component${memberCount === 1 ? '' : 's'}`
                }
                primaryTypographyProps={{
                  noWrap: true,
                  sx: {
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  },
                }}
                secondaryTypographyProps={{
                  noWrap: true,
                  sx: { fontSize: 11, color: theme.palette.text.secondary },
                }}
              />
              <Box
                className="group-row-actions"
                onClick={(e) => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 1 }}
              >
                <SGTooltip title="Rename / recolor" placement="top">
                  <IconButton
                    data-ph-capture-attribute-element="canvas-group-edit"
                    size="small"
                    onClick={() => {
                      closeMenu();
                      setEditingGroup(group);
                    }}
                    aria-label={`Edit ${group.name}`}
                    sx={{
                      p: 0.25,
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </SGTooltip>
                <SGTooltip title="Delete group" placement="top">
                  <IconButton
                    data-ph-capture-attribute-element="canvas-group-delete"
                    size="small"
                    onClick={() => {
                      closeMenu();
                      setPendingDelete(group);
                    }}
                    aria-label={`Delete ${group.name}`}
                    sx={{
                      p: 0.25,
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.error.main,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </SGTooltip>
              </Box>
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 0.5, mx: 0.5 }} />

        <MenuItem
          onClick={() => {
            closeMenu();
            setCreateOpen(true);
          }}
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 26, color: 'inherit' }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="New group"
            primaryTypographyProps={{ sx: { fontSize: 13, fontWeight: 600 } }}
          />
        </MenuItem>
      </Menu>

      {/* Create */}
      <EditGroupDialog
        open={createOpen}
        title="Create a new group"
        initialName=""
        initialColor={pickDefaultColor()}
        onSubmit={(name, color) => {
          setCreateOpen(false);
          void createGroup(name, color).then((created) => {
            if (created != null) {
              setActiveGroupId(created.id);
            }
          });
        }}
        onClose={() => setCreateOpen(false)}
      />

      {/* Per-group rename / recolor (dialog editing from the dropdown) */}
      <EditGroupDialog
        open={editingGroup != null}
        title="Edit group"
        initialName={editingGroup?.name ?? ''}
        initialColor={editingGroup?.color ?? '#42a5f5'}
        onSubmit={(name, color) => {
          if (editingGroup == null) {
            return;
          }
          const prev = editingGroup;
          setEditingGroup(null);
          if (name !== prev.name) {
            void renameGroup(prev.id, name);
          }
          if (color.toLowerCase() !== prev.color.toLowerCase()) {
            void recolorGroup(prev.id, color);
          }
        }}
        onClose={() => setEditingGroup(null)}
      />

      {/* Mobile editor */}
      <GroupEditSheet
        open={sheetOpen && effectiveGroup != null}
        group={effectiveGroup}
        currentSym={
          effectiveGroup != null ? (getGroupSym(effectiveGroup.id) ?? '') : ''
        }
        symOptions={symOptions}
        validSymbols={validSymbolSet}
        onClose={() => setSheetOpen(false)}
        onRename={(name) => {
          if (effectiveGroup != null) {
            void renameGroup(effectiveGroup.id, name);
          }
        }}
        onRecolor={(color) => {
          if (effectiveGroup != null) {
            void recolorGroup(effectiveGroup.id, color);
          }
        }}
        onPickSym={(sym) => {
          if (effectiveGroup != null) {
            broadcastSymToGroup(effectiveGroup.id, sym);
          }
        }}
        onDelete={() => {
          setSheetOpen(false);
          if (effectiveGroup != null) {
            setPendingDelete(effectiveGroup);
          }
        }}
      />

      {/* Delete confirmation */}
      <Dialog
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>
          Delete this group?
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              fontSize: 13.5,
              lineHeight: 1.6,
              color: theme.palette.text.secondary,
            }}
          >
            <Box
              component="span"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              &quot;{pendingDelete?.name}&quot;
            </Box>{' '}
            will be removed. Linked components keep their last symbol but
            won&apos;t stay in sync anymore. This can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setPendingDelete(null)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const id = pendingDelete?.id;
              setPendingDelete(null);
              if (id != null) {
                void deleteGroup(id);
              }
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CanvasGroupsBar;
